import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, FileText, Lock, Unlock, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import {
  useApuracoesIcms,
  useApuracoesPisCofins,
  useApuracoesIrpjCsll,
  useCalcularApuracao,
  useFecharApuracao,
  useReabrirApuracao,
} from "@/features/contabil/hooks/useApuracoes";
import {
  REGIME_IRPJ_LABEL,
  REGIME_PIS_COFINS_LABEL,
  STATUS_LABEL,
  type StatusApuracao,
  type TipoApuracao,
} from "@/features/contabil/types/apuracao";

const fmtCur = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

const STATUS_COLOR: Record<StatusApuracao, string> = {
  0: "bg-blue-500/10 text-blue-600",
  1: "bg-green-500/10 text-green-600",
  2: "bg-amber-500/10 text-amber-600",
};

function StatusBadge({ status, precisaRecalculo }: { status: StatusApuracao; precisaRecalculo: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <Badge className={STATUS_COLOR[status]}>{STATUS_LABEL[status]}</Badge>
      {precisaRecalculo && (
        <Badge className="bg-red-500/10 text-red-600 gap-1">
          <AlertTriangle className="h-3 w-3" /> Recálculo pendente
        </Badge>
      )}
    </div>
  );
}

export default function Apuracoes() {
  const { user } = useAuth();
  const empresaId = user?.empresa?.id ?? 0;
  const usuarioId = user?.id ?? 0;

  const [ano, setAno] = useState<number>(new Date().getFullYear());
  const [periodoCalcular, setPeriodoCalcular] = useState<string>(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`,
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold">Apurações Fiscais</h1>
          <p className="text-muted-foreground">
            Cálculo automático de ICMS, PIS/COFINS e IRPJ/CSLL a partir das NF-e/NFC-e do período.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Ano</span>
            <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 5 }).map((_, i) => {
                  const a = new Date().getFullYear() - i;
                  return <SelectItem key={a} value={String(a)}>{a}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Tabs defaultValue="icms" className="space-y-4">
        <TabsList>
          <TabsTrigger value="icms">ICMS</TabsTrigger>
          <TabsTrigger value="pis-cofins">PIS/COFINS</TabsTrigger>
          <TabsTrigger value="irpj-csll">IRPJ/CSLL</TabsTrigger>
        </TabsList>

        <TabsContent value="icms">
          <PainelCalculoMensal
            tipo="icms"
            empresaId={empresaId}
            periodo={periodoCalcular}
            setPeriodo={setPeriodoCalcular}
          />
          <TabelaIcms empresaId={empresaId} ano={ano} usuarioId={usuarioId} />
        </TabsContent>

        <TabsContent value="pis-cofins">
          <PainelCalculoMensal
            tipo="pis-cofins"
            empresaId={empresaId}
            periodo={periodoCalcular}
            setPeriodo={setPeriodoCalcular}
          />
          <TabelaPisCofins empresaId={empresaId} ano={ano} usuarioId={usuarioId} />
        </TabsContent>

        <TabsContent value="irpj-csll">
          <PainelCalculoTrimestral
            empresaId={empresaId}
            ano={ano}
          />
          <TabelaIrpjCsll empresaId={empresaId} ano={ano} usuarioId={usuarioId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Painel de cálculo (período mensal) ──────────────────────────────────────

function PainelCalculoMensal({
  tipo, empresaId, periodo, setPeriodo,
}: {
  tipo: TipoApuracao;
  empresaId: number;
  periodo: string;
  setPeriodo: (v: string) => void;
}) {
  const calcular = useCalcularApuracao(tipo);

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5" /> Calcular novo período
        </CardTitle>
      </CardHeader>
      <CardContent className="flex items-end gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Período (yyyy-MM)</span>
          <Input
            type="month"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="w-44"
          />
        </div>
        <Button
          onClick={() => calcular.mutate({ empresaId, periodo })}
          disabled={calcular.isPending || empresaId <= 0}
        >
          {calcular.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
          Calcular
        </Button>
      </CardContent>
    </Card>
  );
}

function PainelCalculoTrimestral({ empresaId, ano }: { empresaId: number; ano: number }) {
  const [trimestre, setTrimestre] = useState<number>(Math.ceil((new Date().getMonth() + 1) / 3));
  const calcular = useCalcularApuracao("irpj-csll");
  const periodo = `${ano}-T${trimestre}`;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5" /> Calcular trimestre
        </CardTitle>
      </CardHeader>
      <CardContent className="flex items-end gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Trimestre</span>
          <Select value={String(trimestre)} onValueChange={(v) => setTrimestre(Number(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4].map((t) => <SelectItem key={t} value={String(t)}>{t}º</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Período</span>
          <span className="text-sm font-mono pt-2">{periodo}</span>
        </div>
        <Button
          onClick={() => calcular.mutate({ empresaId, periodo })}
          disabled={calcular.isPending || empresaId <= 0}
        >
          {calcular.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
          Calcular
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Tabela ICMS ─────────────────────────────────────────────────────────────

function TabelaIcms({ empresaId, ano, usuarioId }: { empresaId: number; ano: number; usuarioId: number }) {
  const { data = [], isLoading } = useApuracoesIcms(empresaId, ano);
  const fechar = useFecharApuracao("icms");
  const reabrir = useReabrirApuracao("icms");

  const totalAPagar = useMemo(
    () => data.filter((a) => a.saldoApurar > 0).reduce((s, a) => s + a.saldoApurar, 0),
    [data],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>ICMS — {ano}</CardTitle>
        {data.length > 0 && (
          <p className="text-sm text-muted-foreground">
            Total a recolher no ano: <strong className="text-red-600">{fmtCur(totalAPagar)}</strong>
          </p>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-32" /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Período</TableHead>
                <TableHead className="text-right">Débitos saídas</TableHead>
                <TableHead className="text-right">Créditos entradas</TableHead>
                <TableHead className="text-right">DIFAL</TableHead>
                <TableHead className="text-right">ICMS-ST</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Nenhuma apuração calculada para {ano}.</TableCell></TableRow>
              ) : data.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono">{a.periodo}</TableCell>
                  <TableCell className="text-right">{fmtCur(a.debitosSaidas)}</TableCell>
                  <TableCell className="text-right">{fmtCur(a.creditosEntradas)}</TableCell>
                  <TableCell className="text-right">{fmtCur(a.difal)}</TableCell>
                  <TableCell className="text-right">{fmtCur(a.icmsST)}</TableCell>
                  <TableCell className={`text-right font-semibold ${a.saldoApurar > 0 ? "text-red-600" : "text-green-600"}`}>
                    {fmtCur(a.saldoApurar)}
                  </TableCell>
                  <TableCell><StatusBadge status={a.status} precisaRecalculo={a.precisaRecalculo} /></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button asChild size="sm" variant="ghost"><Link to={`/contabil/apuracoes/icms/${a.id}`}><FileText className="h-4 w-4" /></Link></Button>
                    {a.status !== 1 ? (
                      <Button size="sm" variant="ghost" onClick={() => fechar.mutate({ id: a.id, usuarioId })} disabled={fechar.isPending}>
                        <Lock className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => reabrir.mutate({ id: a.id, usuarioId })} disabled={reabrir.isPending}>
                        <Unlock className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ── Tabela PIS/COFINS ───────────────────────────────────────────────────────

function TabelaPisCofins({ empresaId, ano, usuarioId }: { empresaId: number; ano: number; usuarioId: number }) {
  const { data = [], isLoading } = useApuracoesPisCofins(empresaId, ano);
  const fechar = useFecharApuracao("pis-cofins");
  const reabrir = useReabrirApuracao("pis-cofins");

  return (
    <Card>
      <CardHeader><CardTitle>PIS/COFINS — {ano}</CardTitle></CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-32" /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Período</TableHead>
                <TableHead>Regime</TableHead>
                <TableHead className="text-right">Receita</TableHead>
                <TableHead className="text-right">PIS apurar</TableHead>
                <TableHead className="text-right">COFINS apurar</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nenhuma apuração calculada para {ano}.</TableCell></TableRow>
              ) : data.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono">{a.periodo}</TableCell>
                  <TableCell>{REGIME_PIS_COFINS_LABEL[a.regime]}</TableCell>
                  <TableCell className="text-right">{fmtCur(a.receitaBruta)}</TableCell>
                  <TableCell className="text-right text-red-600">{fmtCur(a.pisApurar)}</TableCell>
                  <TableCell className="text-right text-red-600">{fmtCur(a.cofinsApurar)}</TableCell>
                  <TableCell><StatusBadge status={a.status} precisaRecalculo={a.precisaRecalculo} /></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button asChild size="sm" variant="ghost"><Link to={`/contabil/apuracoes/pis-cofins/${a.id}`}><FileText className="h-4 w-4" /></Link></Button>
                    {a.status !== 1 ? (
                      <Button size="sm" variant="ghost" onClick={() => fechar.mutate({ id: a.id, usuarioId })} disabled={fechar.isPending}>
                        <Lock className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => reabrir.mutate({ id: a.id, usuarioId })} disabled={reabrir.isPending}>
                        <Unlock className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ── Tabela IRPJ/CSLL ────────────────────────────────────────────────────────

function TabelaIrpjCsll({ empresaId, ano, usuarioId }: { empresaId: number; ano: number; usuarioId: number }) {
  const { data = [], isLoading } = useApuracoesIrpjCsll(empresaId, ano);
  const fechar = useFecharApuracao("irpj-csll");
  const reabrir = useReabrirApuracao("irpj-csll");

  return (
    <Card>
      <CardHeader><CardTitle>IRPJ/CSLL — {ano}</CardTitle></CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-32" /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Período</TableHead>
                <TableHead>Regime</TableHead>
                <TableHead className="text-right">Receita</TableHead>
                <TableHead className="text-right">IRPJ + adicional</TableHead>
                <TableHead className="text-right">CSLL</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nenhuma apuração calculada para {ano}.</TableCell></TableRow>
              ) : data.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono">{a.periodo}</TableCell>
                  <TableCell>{REGIME_IRPJ_LABEL[a.regime]}</TableCell>
                  <TableCell className="text-right">{fmtCur(a.receitaBruta)}</TableCell>
                  <TableCell className="text-right text-red-600">{fmtCur(a.irpjApurar)}</TableCell>
                  <TableCell className="text-right text-red-600">{fmtCur(a.csllApurar)}</TableCell>
                  <TableCell><StatusBadge status={a.status} precisaRecalculo={a.precisaRecalculo} /></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button asChild size="sm" variant="ghost"><Link to={`/contabil/apuracoes/irpj-csll/${a.id}`}><FileText className="h-4 w-4" /></Link></Button>
                    {a.status !== 1 ? (
                      <Button size="sm" variant="ghost" onClick={() => fechar.mutate({ id: a.id, usuarioId })} disabled={fechar.isPending}>
                        <Lock className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => reabrir.mutate({ id: a.id, usuarioId })} disabled={reabrir.isPending}>
                        <Unlock className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
